import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getAdminListing, getRawListing, recheckListingImage,
} from "../api/adminApi";
import {
  StatusBadge, ImageHealthBadge as IHBadge, fmtDate, Section,
} from "../components/admin/AdminUI";
import {
  hasValidUrl,
  collectImageUrls,
  pickHeroUrl,
  shouldShowUnverifiedBadge,
} from "../utils/image";

function KV({ k, v }) {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="flex items-baseline gap-3 py-1 border-b border-zinc-900/60 last:border-0">
      <span className="w-32 text-[10px] uppercase tracking-wider text-zinc-500 flex-shrink-0">{k}</span>
      <span className="text-xs text-zinc-200 break-all">
        {Array.isArray(v) ? v.join(", ") : String(v)}
      </span>
    </div>
  );
}

function ImageGallery({ images, primary, image_health, listing }) {
  const allUrls = collectImageUrls(listing);
  const [active, setActive] = useState(pickHeroUrl(listing));
  const show = hasValidUrl(active);
  const showUnverifiedBadge = shouldShowUnverifiedBadge(image_health) && show;
  return (
    <div className="space-y-2">
      <div className="aspect-[4/3] rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden relative">
        {show ? (
          <>
            <img src={active} alt="" className="absolute inset-0 w-full h-full object-cover" />
            {showUnverifiedBadge && (
              <div
                data-testid="admin-image-unverified-badge"
                className="absolute top-2 right-2 h-6 px-2 rounded-full bg-zinc-900/80 text-zinc-300 text-xs flex items-center border border-zinc-700"
              >
                ? unverified
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-zinc-600 text-xs uppercase tracking-widest">
            {image_health === "dead_on_check" && allUrls.length === 0
              ? "image broken"
              : "no image"}
          </div>
        )}
      </div>
      {allUrls.length > 1 && (
        <div className="grid grid-cols-6 gap-1.5">
          {allUrls.slice(0, 12).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setActive(u)}
              className={`aspect-square rounded overflow-hidden border ${active === u ? "border-emerald-500" : "border-zinc-800"}`}
            >
              <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminListingDetailPage() {
  const { ulid } = useParams();
  const { data: listing, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "listing", ulid],
    queryFn: () => getAdminListing(ulid),
    enabled: Boolean(ulid),
    retry: false,
  });

  const raw = useQuery({
    queryKey: ["admin", "raw-listing", listing?.raw_listing_ulid],
    queryFn: () => getRawListing(listing.raw_listing_ulid),
    enabled: Boolean(listing?.raw_listing_ulid),
    retry: false,
  });

  const recheck = useMutation({
    mutationFn: () => recheckListingImage(ulid),
    onSuccess: () => {
      toast.success("Image re-check triggered");
      refetch();
    },
    onError: (err) => toast.error(String(err?.message || err)),
  });

  const copyRaw = async () => {
    if (!raw.data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(raw.data, null, 2));
      toast.success("Raw payload copied");
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  if (isLoading) return <div className="text-zinc-500">Loading…</div>;
  if (isError)
    return (
      <div data-testid="admin-listing-detail-error" className="text-rose-400 space-y-3">
        {error?.response?.status === 404 ? "Listing not found." : String(error?.message || error)}
        <div><Link to="/admin/listings" className="text-emerald-300 underline text-sm">← back to listings</Link></div>
      </div>
    );
  if (!listing) return null;

  const vertical = listing.vertical;

  return (
    <div data-testid="admin-listing-detail-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/admin/listings" className="text-sm text-zinc-400 hover:text-zinc-200">
          ← Back to listings
        </Link>
        <div className="flex gap-2">
          <Link
            to={`/listing/${listing.id}`}
            className="px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-200 text-xs hover:border-emerald-500/60"
          >
            View public listing →
          </Link>
          {listing.url && (
            <a
              href={listing.url}
              target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-md bg-emerald-500 text-zinc-950 text-xs font-medium"
            >
              Open on {listing.source_platform} ↗
            </a>
          )}
        </div>
      </div>

      <header className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={listing.status} />
          <IHBadge value={listing.image_health} />
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{vertical}</span>
        </div>
        <h1 className="text-2xl text-zinc-100 font-medium leading-tight">{listing.title}</h1>
        <div className="text-sm text-amber-400">{listing.price_display}</div>
        <div className="font-mono text-[11px] text-zinc-500 break-all">{listing.id}</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: canonical mapped view */}
        <div className="space-y-5">
          <ImageGallery
            images={listing.images} primary={listing.primary_image_url}
            image_health={listing.image_health}
            listing={listing}
          />

          <Section title="Canonical listing">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3">
              <KV k="Source platform" v={listing.source_platform} />
              <KV k="Source market" v={listing.source_market} />
              <KV k="Country" v={listing.country} />
              <KV k="Location" v={listing.location} />
              <KV k="Currency" v={listing.currency} />
              <KV k="First seen" v={fmtDate(listing.created_at)} />
              <KV k="Last seen" v={fmtDate(listing.last_seen_at)} />
              <KV k="Mapping adapter" v={listing.mapping_adapter} />
            </div>
          </Section>

          {vertical === "auto" && (
            <Section title="Auto details">
              <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3">
                <KV k="Make" v={listing.make} />
                <KV k="Model" v={listing.model} />
                <KV k="Model family" v={listing.model_family} />
                <KV k="Model series" v={listing.model_series} />
                <KV k="Series match" v={listing.series_match_level} />
                <KV k="Series confidence" v={listing.series_match_confidence} />
                <KV k="Year" v={listing.year} />
                <KV k="Mileage (km)" v={listing.mileage_km} />
                <KV k="Fuel" v={listing.fuel} />
                <KV k="Gearbox" v={listing.gearbox} />
                <KV k="Power (kW)" v={listing.power_kw} />
                <KV k="Body type" v={listing.body_type} />
                <KV k="Risk flags" v={listing.risk_flags} />
              </div>
            </Section>
          )}

          {vertical === "immo" && (
            <Section title="Immo details">
              <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3">
                <KV k="Transaction" v={listing.transaction_type} />
                <KV k="Object type" v={listing.object_type} />
                <KV k="Living area m²" v={listing.living_area_m2} />
                <KV k="Weighted area m²" v={listing.weighted_area_m2} />
                <KV k="Rooms" v={listing.rooms} />
                <KV k="Price / m²" v={listing.price_per_m2} />
                <KV k="Outdoor space" v={listing.has_outdoor_space} />
                <KV k="Feature slugs" v={listing.feature_slugs} />
              </div>
            </Section>
          )}

          <Section title="Image health">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3 space-y-1">
              <KV k="Status" v={listing.image_health} />
              <KV k="Strikes" v={listing.image_health_strikes} />
              <KV k="Last checked" v={fmtDate(listing.image_health_checked_at)} />
            </div>
            <button
              type="button"
              onClick={() => recheck.mutate()}
              disabled={recheck.isPending}
              data-testid="admin-recheck-image-btn"
              className="mt-2 px-3 py-1.5 text-xs rounded-md border border-zinc-700 text-zinc-300 hover:border-emerald-500/60 disabled:opacity-40"
            >
              {recheck.isPending ? "Re-checking…" : "Re-check image now"}
            </button>
          </Section>

          {Array.isArray(listing.mapping_warnings) && listing.mapping_warnings.length > 0 && (
            <Section title="Mapping warnings">
              <div className="flex flex-wrap gap-1.5">
                {listing.mapping_warnings.map((w, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px]"
                  >
                    ⚠ {w}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* RIGHT: raw payload */}
        <div className="space-y-2">
          <Section
            title="Raw Apify payload"
            action={
              <button
                type="button"
                onClick={copyRaw}
                disabled={!raw.data}
                data-testid="admin-copy-raw-btn"
                className="text-xs text-emerald-300 hover:underline disabled:opacity-40"
              >
                Copy JSON
              </button>
            }
          >
            <div
              data-testid="admin-raw-payload"
              className="rounded-lg border border-zinc-800 bg-zinc-950/60 max-h-[80vh] overflow-auto"
            >
              {raw.isLoading ? (
                <div className="p-4 text-zinc-500 text-xs">Loading raw payload…</div>
              ) : raw.isError ? (
                <div className="p-4 text-rose-400 text-xs">
                  {raw.error?.response?.status === 404
                    ? "Raw listing missing in DB."
                    : String(raw.error?.message || raw.error)}
                </div>
              ) : (
                <pre className="p-4 text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-all">
                  {JSON.stringify(raw.data, null, 2)}
                </pre>
              )}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
